import { ArrowLeft, ArrowRight, Brain, CheckCircle2, CircleHelp, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { postJson, request } from "../api";
import { ErrorCard, Loading } from "../components/AsyncState";
import type { AssessmentItem, AssessmentResult, AssessmentSession } from "../types";
import { errorMessage, formatNumber } from "../utils";

export function AssessmentPage() {
  const [session, setSession] = useState<AssessmentSession | null>(null);
  const [item, setItem] = useState<AssessmentItem | null>(null);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const shownAt = useRef(performance.now());

  async function begin() {
    setBusy(true);
    setResult(null);
    setError("");
    try {
      const started = await postJson<AssessmentSession>("/api/assessment", {});
      setSession(started);
      setItem(await request<AssessmentItem>(`/api/assessment/${started.id}/next`));
      shownAt.current = performance.now();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => void begin(), []);

  async function answer(answeredReal: boolean) {
    if (!session || !item || busy) return;
    setBusy(true);
    try {
      const response = await postJson<{ next_item: AssessmentItem | null }>(
        `/api/assessment/${session.id}/responses`,
        {
          item_key: item.key,
          answered_real: answeredReal,
          response_ms: Math.round(performance.now() - shownAt.current)
        }
      );
      if (response.next_item) {
        setItem(response.next_item);
        shownAt.current = performance.now();
      } else {
        setItem(null);
        setResult(await postJson<AssessmentResult>(`/api/assessment/${session.id}/finish`, {}));
      }
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  if (error && !session) return <ErrorCard message={error} retry={() => void begin()} />;
  if (!session) return <Loading label="正在准备词汇测试" />;

  return (
    <div className="assessment-page">
      <Link className="back-link" to="/"><ArrowLeft size={16} /> 返回今日</Link>
      <header>
        <span className="eyebrow"><Brain size={14} /> Flussik lexical decision v1</span>
        <h1>{result ? "初始范围已建立" : "这个是法语词吗？"}</h1>
        <p>{result ? "它会随着真实阅读继续校准。" : "凭第一感觉判断，不查词，也不用反复推敲。"}</p>
      </header>
      {result ? (
        <section className="assessment-result">
          <CheckCircle2 size={34} />
          <span>实验性估算范围</span>
          <strong>{formatNumber(result.estimated_lower)}–{formatNumber(result.estimated_upper)}</strong>
          <small>接受性 lemma 范围 · 置信度 {result.confidence}</small>
          <div className="assessment-result__stats">
            <span>真实词识别 <strong>{result.words_hit}/{result.words_total}</strong></span>
            <span>误判伪词 <strong>{result.false_alarms}/{result.nonwords_total}</strong></span>
          </div>
          <p>{result.interpretation}</p>
          <div className="button-row">
            <Link className="primary-button" to="/library">选择一本书 <ArrowRight size={17} /></Link>
            <button className="secondary-button" onClick={() => void begin()}><RotateCcw size={16} /> 重新测试</button>
          </div>
        </section>
      ) : item ? (
        <section className="assessment-card">
          <div className="assessment-progress">
            <span>{item.position} / {item.total}</span>
            <div><i style={{ width: `${(item.position / item.total) * 100}%` }} /></div>
          </div>
          <div className="assessment-word">{item.text}</div>
          <div className="assessment-actions">
            <button disabled={busy} onClick={() => void answer(false)}>
              <CircleHelp size={21} /> 不是法语词
            </button>
            <button disabled={busy} onClick={() => void answer(true)}>
              <CheckCircle2 size={21} /> 是法语词
            </button>
          </div>
          <small>系统不会在测试中告诉你对错，避免影响后续项目。</small>
        </section>
      ) : (
        <Loading label="正在计算范围" />
      )}
      {error && <ErrorCard message={error} />}
    </div>
  );
}
